import React, {useEffect, useState} from 'react';
import {styled} from '@mui/material/styles';
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from 'react-i18next';
import axios from "axios";

import { LicenseInfo } from '@mui/x-license-pro';
LicenseInfo.setLicenseKey(
    'd1cfba003df8b29eeba03035d66213dcT1JERVI6NDM1MzksRVhQSVJZPTE2ODM5MDM3NzAwMDAsS0VZVkVSU0lPTj0x',
);

// Components
import SmartDataGridToolbar from "./SmartDataGridToolbar";

import {
    useLocation
} from 'react-router-dom'

import {appSetAlert, appSetModalContent} from "../../../../redux/app/appActions";
import {findComponent} from "../../../../redux/page/pageActions";

import SettingsForm from "../settings/SettingsForm";

import useFeature from "../../../../hooks/useFeature";
import useDataSource from "../../../../hooks/useDataSource";

import {
    formatDate,
    formatDuration,
    formatNumber,
    formatStyle,
    formatText,
    formatSuffix, formatPrefix, formatAvatar, FormatLink, FormatModalLink, formatGoodBad
} from "../../../../utilities/FormatUtilities";
import {
    getFieldAlias,
    getFieldFormatters
} from "../../../../utilities/DataSourceUtilities";

import {axiosDelay} from "../../../../config/config";
import {
	dataAddFilter,
	dataReloadSources,
	dataRemoveFilter,
	filterUpdateConditionAction
} from "../../../../redux/data/dataActions";
import LinkIcon from "../../../application/LinkIcon";
import ConfirmationButton from "./SmartDataGridConfirmationButton";

import {LinearProgress, Card, CardHeader, CardContent, Tooltip} from "@mui/material";
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {
    GridRowsProp,
    useGridApiRef,
    DataGridPro,
    GridApi,
    GridColumns,
    GridRowParams,
    MuiEvent,
    GridToolbarContainer,
    GridActionsCellItem,
    GridEventListener,
    GridEvents,
    GridRowId,
    GridRowModel,
} from '@mui/x-data-grid-pro';

const PREFIX = 'SmartDataGrid';

const classes = {
    root: `${PREFIX}-root`,
    grid: `${PREFIX}-grid`,
    noLayout: `${PREFIX}-noLayout`,
    pointer: `${PREFIX}-pointer`,
    linearProgress: `${PREFIX}-linearProgress`,
    card: `${PREFIX}-card`,
    cardHeader: `${PREFIX}-cardHeader`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: 'calc(100% - 1px)'
    },

    [`& .${classes.grid}`]: {
        border: 0,
        '& .MuiDataGrid-columnHeaderTitle': {
            textOverflow: "clip",
            whiteSpace: "break-spaces",
            lineHeight: 1.5
        },
        // '& .MuiDataGrid-cell': {
        //     textOverflow: "clip",
        //     whiteSpace: "break-spaces",
        //     lineHeight: 1.25
        // },
        // Line height workaround
        // '& .MuiDataGrid-renderingZone': {
        //     maxHeight: 'none !important',
        // },
        // '& .MuiDataGrid-cell': {
        //     lineHeight: 'unset !important',
        //     maxHeight: 'none !important',
        //     whiteSpace: 'normal',
        // },
        // '& .MuiDataGrid-row': {
        //     maxHeight: 'none !important',
        // }
    },

    [`& .${classes.noLayout}`]: {
        width: '100%',
        height: '100%',
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        margin: '0px',
        padding: '5px'
    },

    [`& .${classes.pointer}`]: {
        cursor: 'pointer'
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.card}`]: {
        height: '100%',
        // padding: '8px',
        boxShadow: 'none',
        backgroundColor: theme.palette.background.default
    },

    [`& .${classes.cardHeader}`]: {
        padding: '8px 8px 0px 8px',
        alignItems: 'start',
        textAlign: 'left',
        textTransform: 'none'
    }
}));

export default function SmartDataGrid({id, configuration, xId, onLoading, onLoaded}) {

    const dispatch = useDispatch();
    const {t} = useTranslation();

    // App State
    const pageID = useSelector(state => state.app.pageID);
    const dataDate = useSelector(state => state.data.dataDate);
    const editXId = useSelector(state => state.page.componentEdit.xId);
	const filters = useSelector(state => state.data.filters);
    const componentUpdated = useSelector(state => state.page.componentUpdated);
    const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
    const pages = useSelector(state => state.page.pages);
    const pageConfigs = useSelector(state => state.config.data.pageConfigs);
    const activeRole = useSelector(state => state.persist.activeRoleName);
    const config = useSelector(state => state.config)

	// Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	const titleV = compConfig.name?.titleVariant ? compConfig.name.titleVariant : "subtitle2";
	const subTitleV = compConfig.name?.subTitleVariant ? compConfig.name.subTitleVariant : "body2";
	const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});

    const dataSourceName = compConfig.dataSource?.name ? compConfig.dataSource?.name : '';
	const alwaysLoadDataSource = compConfig.dataSource?.alwaysLoadDataSource ? compConfig.dataSource.alwaysLoadDataSource : false;
    const idField = compConfig.dataSource?.idField ? compConfig.dataSource.idField : null;
    const fields = compConfig.dataSource?.fields ? compConfig.dataSource.fields : [];
    const editable = compConfig.dataSource?.fields.editable ? compConfig.dataSource.fields.editable : false;
    const [columns, setColumns] = React.useState([]);
    const [rows, setRows] = React.useState([]);

    // Element
    const rowsPerPageOptions = compConfig.element?.rowsPerPageOptions ? compConfig.element.rowsPerPageOptions : [5, 10, 25];
    const defaultRowsPerPage = compConfig.element?.defaultRowsPerPage ? compConfig.element.defaultRowsPerPage : 5;
    const selectionOnClick = compConfig.element?.selectionOnClick.enabled ? compConfig.element.selectionOnClick.enabled : false;
    const columnMenu = compConfig.element?.columnMenu.enabled ? compConfig.element.columnMenu.enabled : false;
    const styles = compConfig?.element?.styles;

    // Element -- Toolbar
    const toolbar = compConfig.element?.toolbar.enabled ? compConfig.element.toolbar.enabled : false;
    const toolbarColumn = compConfig.element?.toolbar.commands.columnSelector.enabled ? compConfig.element.toolbar.commands.columnSelector.enabled : false;
    const toolbarFilter = compConfig.element?.toolbar.commands.columnFilter.enabled ? compConfig.element.toolbar.commands.columnFilter.enabled : false;
    const toolbarDensity = compConfig.element?.toolbar.commands.densitySelector.enabled ? compConfig.element.toolbar.commands.densitySelector.enabled : false;
    const toolbarExport = compConfig.element?.toolbar.commands.exportOption.enabled ? compConfig.element.toolbar.commands.exportOption.enabled : false;
    const toolbarExportPrint = compConfig.element?.toolbar.commands.exportOption.printOption.enabled ? compConfig.element.toolbar.commands.exportOption.printOption.enabled : false;
    const toolbarExportCsv = compConfig.element?.toolbar.commands.exportOption.csvOption.enabled ? compConfig.element.toolbar.commands.exportOption.csvOption.enabled : false;
    const toolbarOptions = { toolbarColumn, toolbarFilter, toolbarDensity, toolbarExport, toolbarExportPrint, toolbarExportCsv }

    // Element -- Editing
    const addRows = compConfig.element?.editing.add.enabled ? compConfig.element.editing.add.enabled : false;
    const updateRows = compConfig.element?.editing.update.enabled ? compConfig.element.editing.update.enabled : false;
    const deleteRows = compConfig.element?.editing.delete.enabled ? compConfig.element.editing.delete.enabled : false;

	// Element -- Default Sorting
	const sort = compConfig.element?.sort ? compConfig.element.sort : null;
	let defaultSort = {
		field: "id",
		sort: null
	};
	if (sort !== null) {
		defaultSort = {
			field: sort.orderBy,
			sort: sort.order
		}
	}
	const [sortModel, setSortModel] = useState([defaultSort])

    let dataSource = useDataSource(dataSourceName);

    // When using Args i.e. :list
    let list = useLocation().pathname;
    list = /.*\/([^?]+)/.exec(list)[1];
    const feature = useFeature(list);
    const featureTarget = feature ? feature.options?.featureOptions?.target?.name : '';

	useEffect(() => {
		if (alwaysLoadDataSource) {
			const interval = setInterval(() => {
				dispatch(dataReloadSources([dataSource.source]));
			}, 20000);

			return () => clearInterval(interval);
		}
	},[])

    // Loading
    useEffect(() => {
        if (dataSource.fetching === true) {
            if (isFunction(onLoading)) {
                onLoading();
            }
			setShowProgress(true);
        }
    }, [dataSource.fetching]);

    // Loaded
    useEffect(() => {
        if (dataSource.fetching === false && dataSource.fetched === true) {
            if (isFunction(onLoaded)) {
                onLoaded();
            }
			setShowProgress(false);
        }
    }, [dataSource.fetched]);

    // Did Component Wrapper Trigger Redux State Change?
    useEffect(() => {
        if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
            if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
                const page = pages.find(page => page.name === pageID);
                for (let i = 0; i < pageConfigs.length; i++) {
                    const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
                    if (roleConfig) {
                        const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
                        if (findResult) {
                            setCompConfig(findResult.configuration);
                            break;
                        }
                    }
                }
            }
        }
    }, [componentWrapperEdit]);

    // Component Configuration (compConfig) changed, reset state
    useEffect(() => {
        setTitle(compConfig.name?.title ? compConfig.name.title : '');
        setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
    }, [compConfig]);

    // Component Updated & Resized
    useEffect(() => {
        if (xId) {
            if (componentUpdated.id === xId) {
                setCompConfig(componentUpdated.config);
            }
        }
    }, [componentUpdated]);

    // Title & Subtitle
    useEffect(() => {
        resizeCard();
    }, [title, subtitle]);

    // Data
    useEffect(() => {

        function formatValue(formattedValue, formatterUsed, row) {
            let isIconic = false;
            for (const formatter of formatterUsed) {
                if (formatter.type.toUpperCase() === 'AVATAR') {
                    formattedValue = formatAvatar(formattedValue, formatter.config);
                    isIconic = true;
                    break;
                } else if (formatter.type.toUpperCase() === 'DATE') {
                    formattedValue = formatDate(formattedValue, formatter.config);
                    // formattedValue = DateUtilities.dateString(formattedValue);
                } else if (formatter.type.toUpperCase() === 'NUMBER') {
                    formattedValue = formatNumber(formattedValue, formatter.config);
                } else if (formatter.type.toUpperCase() === 'STYLE') {
                    formattedValue = formatStyle(formattedValue, formatter.config, formattedValue, formatter.condition);
                } else if (formatter.type.toUpperCase() === 'TEXT') {
                    formattedValue = formatText(formattedValue, formatter.config);
                } else if (formatter.type.toUpperCase() === 'LINK') {
                    formattedValue = FormatLink(formattedValue, formatter.config);
                    isIconic = true;
                    break;
                } else if (formatter.type.toUpperCase() === 'MODAL') {
                    formattedValue = FormatModalLink(formattedValue, formatter.config);
                    // Pulled from SmartTable.js. Revisit if needed
                    // } else if (formatter.type.toUpperCase() === 'DELETE') {
                    //     formattedValue = <ConfirmationButton type="delete" tooltip={formatter.config.tooltip} handleConfirm={() => { deleteRow(row, formatter.config) }}/>
				} else if (formatter.type.toUpperCase() === 'FILTER') {
					// formattedValue = <ConfirmationButton type="filter" tooltip={formatter.config.tooltip} handleConfirm={() => { filterRow(row, formatter.config) }}/>
					formattedValue = <Tooltip title={"Select Row"} arrow placement="top-start"><FilterAltIcon style={{cursor: 'pointer'}} onClick={() => { filterRow(row, formatter.config) }}></FilterAltIcon></Tooltip>
					isIconic = true;
					break;
				} else if (formatter.type.toUpperCase() === 'FILTERLINK') {
					formattedValue = <div style={{cursor: 'pointer'}} onClick={() => { filterRow(row, formatter.config) }}><LinkIcon link={formatter.config.link}></LinkIcon></div>
					isIconic = true;
					break;
                } else if (formatter.type.toUpperCase() === 'SUFFIX') {
                    formattedValue = formatSuffix(formattedValue, formatter.config);
                } else if (formatter.type.toUpperCase() === 'GOODBAD') {
                    formattedValue = formatGoodBad(formattedValue, formatter.config, formattedValue, formatter.condition);
                } else if (formatter.type.toUpperCase() === 'DURATION') {
                    if (dataSource.data.length > 0) {
                        formattedValue = formatDuration(dataSource.data[0].min, dataSource.data[0].max, formatter.config);
                    }
                } else {

                }
            }
            return formattedValue;
        }

        if (dataSource.error) {
            // TODO: No Data Message
        } else if (dataSource.data && dataSource.fetched) {

            const data = dataSource.data;
            const newColumns: GridColumns = [];
            // const columns = [];
            for (let f of fields) {
                // Lookup the alias and formatters
                const label = getFieldAlias(dataSource, f.name);
                const alias = label !== null ? label : f.name;
                const formatters = getFieldFormatters(dataSource, f.name);
                const formatterUsed = f.formatters.length > 0 ? f.formatters : formatters;
                newColumns.push(
                    {
                        headerName: alias
                        , field: f.name
                        , type: f.type
                        , valueOptions: f.valueOptions ? f.valueOptions : null
                        , editable: f.editable
                        , minWidth: f.minWidth
                        , flex: f.flex
                        , headerAlign: f.headerAlign
                        , align: f.align
                        // , hideSortIcons: true
                        // , disableColumnMenu: true
                        , formatters: formatterUsed
                        , renderCell: (params) => {
                            return formatValue(params.value, formatterUsed, params.row)
                        }
                    }
                )
            }
            // Add actions column
            if (updateRows || deleteRows) {
                newColumns.push(
                    {
                        headerName: 'Actions',
                        field: 'actions',
                        type: 'actions',
                        minWidth: 100,
                        flex: 1,
                        cellClassName: 'actions',
                        getActions: ({ id, row}) => {
                            const isInEditMode = apiRef.current.getRowMode(id) === 'edit';

                            if (isInEditMode) {
                                return [
                                    <GridActionsCellItem
                                        icon={<SaveIcon />}
                                        label="Save"
                                        onClick={handleSaveClick(id)}
                                        color="primary"
                                    />,
                                    <GridActionsCellItem
                                        icon={<CancelIcon />}
                                        label="Cancel"
                                        className="textPrimary"
                                        onClick={handleCancelClick(id)}
                                        color="inherit"
                                    />,
                                ];
                            } else {
                                let actions = [];
                                if (updateRows) {
                                    actions.push(
                                        <GridActionsCellItem
                                            icon={<EditIcon />}
                                            label="Edit"
                                            className="textPrimary"
                                            onClick={handleEditClick(id)}
                                            color="inherit"
                                        />)
                                }
                                if (deleteRows) {
                                    actions.push(<ConfirmationButton tooltip="Delete" handleConfirm={handleDeleteClick(id, row)}/>)
                                }
                                return actions;
                            }
                        },
                    }
                )
            }

            setColumns(newColumns);

            const newRows: GridRowsProp = [];
            for (let d of data) {
                const newRow = {};
                for (let f = 0; f < fields.length; f++) {
                    newRow[fields[f].name] = d[fields[f].name];
                }
                newRow.id = d[idField];
                newRows.push(newRow);
            }
            setRows(newRows);
        }
    }, [dataSource.data]);

    // xEdit
    useEffect(() => {
        if (editXId) {
            if ((editXId === xId) && (pageID)) {

                const placeholders = {
                    title: title,
                    subtitle: subtitle
                }

                const settingsConfig = {
                    title: 'Data Grid Configuration',
                    parent: {
                        minWidth: '400px',
                        maxWidth: '400px',
                        minHeight: '520px',
                        maxHeight: '520px'
                    },
                    paper: null,
                    button: null,
                    content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'DataGrid'}
                                           placeholders={placeholders}/>,
                    tooltipContent: null
                }
                dispatch(appSetModalContent(settingsConfig));
            }
        }
    }, [editXId, pageID]);

    function isFunction(functionToCheck) {
        return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
    }

    const resizeCard = () => {

        const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
        const newCardContentStyle = Object.assign({}, {
            ...styles?.cardContent
            , height: 'calc(100% - 49px)'
            , overflow: 'hidden'
            , padding: '0px'
            , display: 'flex'
            , flexDirection: 'column'
            , justifyContent: 'center'
        });

        if (title && subtitle) {
            // Default Style
        } else if (title) {
            // Title Only
            newCardHeaderStyle.height = '29px';
            newCardContentStyle.height = 'calc(100% - 29px)';
        } else if (subtitle) {
            // Subtitle Only
            newCardHeaderStyle.height = '29px';
            newCardContentStyle.height = 'calc(100% - 29px)';
        } else {
            // No title
            newCardHeaderStyle.height = '0px';
            newCardHeaderStyle.padding = '0px';
            newCardContentStyle.height = '100%';
        }
        setCardHeaderStyle(newCardHeaderStyle);
        setCardContentStyle(newCardContentStyle);
    }

    interface EditToolbarProps {
        apiRef: React.MutableRefObject<GridApi>
    }

    function AddButton(props: EditToolbarProps) {
        const { apiRef } = props;

        // Add row
        const handleClick = () => {
            apiRef.current.updateRows([{ id, isNew: true }]);
            apiRef.current.startRowEditMode({ id });

            setTimeout(() => {
                apiRef.current.scrollToIndexes({
                    rowIndex: apiRef.current.getRowsCount() - 1,
                });
                apiRef.current.setCellFocus(id, 'name');
            });
        }

        return (
            <Button sx={{position: 'absolute', right: 0}} color="primary" startIcon={<AddIcon />} onClick={handleClick}>
                Add record
            </Button>
        )
    }

    const apiRef = useGridApiRef();

    const handleRowEditStart = (
        params: GridRowParams,
        event: MuiEvent<React.SyntheticEvent>,
    ) => {
        event.defaultMuiPrevented = true;
    };

    const handleRowEditStop: GridEventListener<GridEvents.rowEditStop> = (
        params,
        event,
    ) => {
        event.defaultMuiPrevented = true;
    };

    const handleEditClick = (id: GridRowId) => (event: React.MouseEvent) => {
        event.stopPropagation();
        apiRef.current.startRowEditMode({ id });
    };

    const handleSaveClick = (id: GridRowId) => async (event: React.MouseEvent) => {
        event.stopPropagation();
        await apiRef.current.stopRowEditMode({ id });
    };

    // Delete row
    const handleDeleteClick = (id: GridRowId, row: GridRowModel) => (event: React.MouseEvent) => {
        setTimeout(function () {
            event.stopPropagation();
            // Get id field name and value
            let idField = '';
            let idValue = '';
            for (const field in row) {
                // Not the default data grid id. Use the id from the db
                if (field === compConfig.dataSource.idField) {
                    idField = field;
                    idValue = row[field].toString();
                    break;
                }
            }

            // Set api url and body
            const apiUrl = "{0}data?db={1}&table={2}".format(config.data.authentication.api, config.data.authentication.apiSource, dataSource.source);
            const deleteBody = {
                uniqueField: idField,
                uniqueValue: idValue
            }

            axios
                .delete(apiUrl, {
                    data: deleteBody
                })
                .then(function (response) {
                    dispatch(appSetAlert(null, '{0} was successfully deleted.'.format(id), 'success', 10000));
                    dispatch(dataReloadSources([dataSource.source]))
                    // console.log(response)
                })
                .catch(function (error) {
                    console.log('**ERROR**', error);

                    dispatch(appSetAlert('Whoops!', 'Something went seriously wrong. Please try again.', 'error', 10000));
                });
            apiRef.current.updateRows([{ id, _action: 'delete' }]);
        }, JSON.parse(axiosDelay()));
    };

    const handleCancelClick = (id: GridRowId) => async (event: React.MouseEvent) => {
        event.stopPropagation();
        await apiRef.current.stopRowEditMode({ id, ignoreModifications: true });

        const row = apiRef.current.getRow(id);
        if (row.isNew) {
            apiRef.current.updateRows([{ id, _action: 'delete' }]);
        }
    };

    // Add or edit row data
    const processRowUpdate = async (newRow: GridRowModel) => {
        setTimeout(function () {
            let requestBody = {
                fieldValues: []
            }

            for (const field in newRow) {
                if (field !== 'id' && newRow[field] !== null && newRow[field] !== undefined && field !== 'isNew' && field !== compConfig.dataSource.idField) {
                    requestBody.fieldValues.push({
                        field: field,
                        value: newRow[field]
                    })
                } else if (!newRow.isNew && field === compConfig.dataSource.idField) {
                    requestBody = {uniqueField: field, uniqueValue: newRow[field], ...requestBody}
                }
            }

            // Set api url and body
            const apiUrl = "{0}data?db={1}&table={2}".format(config.data.authentication.api, config.data.authentication.apiSource, dataSource.source);

            if (newRow.isNew) {
                // POST request to add a row
                axios
                    .post(apiUrl, requestBody)
                    .then(function (response) {
                        dispatch(appSetAlert(null, 'Row was successfully added.', 'success', 10000));
                        dispatch(dataReloadSources([dataSource.source]))
                        // console.log(response)
                    })
                    .catch(function (error) {
                        console.log('**ERROR**', error);

                        dispatch(appSetAlert('Whoops!', 'Something went seriously wrong. Please try again.', 'error', 10000));
                    });
            } else if (!newRow.isNew) {
                // PUT request to update row
                axios
                    .put(apiUrl, requestBody)
                    .then(function (response) {
                        dispatch(appSetAlert(null, 'Row with unique id "{0}" was successfully updated.'.format(newRow.id), 'success', 10000));
                        dispatch(dataReloadSources([dataSource.source]))
                        // console.log(response)
                    })
                    .catch(function (error) {
                        console.log('**ERROR**', error);

                        dispatch(appSetAlert('Whoops!', 'Something went seriously wrong. Please try again.', 'error', 10000));
                    });
            }
        }, JSON.parse(axiosDelay()));

        return { ...newRow, isNew: false };
    };

	// Filter Row via a field Formatter
	const filterRow = (row, filterConfig) => {
		const filterID = dataSourceName + '-' + filterConfig.idField;

		// Check if SmartDataGrid is filtered
		const localFilter = filters.find(function (f) {
			return f.id.toString().toLowerCase() === filterID.toString().toLowerCase();
		});

		if (localFilter) {
			// Remove the Filter
			dispatch(dataRemoveFilter(localFilter.id));
		} else {
			// Add a Filter
			dispatch(dataAddFilter(filterID, filterConfig.idField, " eq '{0}'".format(row[filterConfig.idField]), filterUpdateConditionAction.REPLACE, true, false));
		}
	};

    return (
        <Root className={classes.root}>
            {showProgress && dataSource.fetching &&
                <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !dataSource.fetching &&
                <div className={classes.linearProgress}></div>
            }
            <Card className={classes.card}>
                { addRows &&
                    <AddButton apiRef={ apiRef }/>
                }
                <CardHeader className={classes.cardHeader} style={cardHeaderStyle}
                            titleTypographyProps={{color: 'textPrimary', variant: titleV}}
                            title={t(title)}
                            subheaderTypographyProps={{color: 'textSecondary', variant: subTitleV}}
                            subheader={t(subtitle)}>
                </CardHeader>
                <CardContent style={cardContentStyle}>
                    <div style={{height: '100%', width: '100%'}}>
                        {toolbar &&
                            <DataGridPro className={classes.grid} rows={rows} columns={columns} apiRef={apiRef} editMode="row" experimentalFeatures={{ newEditingApi: true }}
                                         onRowEditStart={handleRowEditStart} onRowEditStop={handleRowEditStop} processRowUpdate={processRowUpdate}
                                         components={{Toolbar: SmartDataGridToolbar}} componentsProps={{toolbar: toolbarOptions }}
										 sortModel={sortModel} onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
										 disableSelectionOnClick={!selectionOnClick} disableColumnMenu={!columnMenu}/>
                        }
                        {!toolbar &&
                            <DataGridPro className={classes.grid} rows={rows} columns={columns} apiRef={apiRef} editMode="row" experimentalFeatures={{ newEditingApi: true }}
                                         onRowEditStart={handleRowEditStart} onRowEditStop={handleRowEditStop} processRowUpdate={processRowUpdate}
										 sortModel={sortModel} onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
										 disableSelectionOnClick={!selectionOnClick} disableColumnMenu={!columnMenu}/>
                        }
                    </div>
                </CardContent>
            </Card>
        </Root>
    );
}
